import styled from '@emotion/styled'
import {
    faCalendarDays,
    faCashRegister,
    faExternalLink,
    faHomeLg,
    faKey,
    faMedal,
    faNewspaper,
    faRobot,
    faUser,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import Breakpoint from '../../Breakpoint'
import UserContext from '../../context/UserContext'
import { useLangSelected } from '../../hooks/useLangSelected'
import AdminContainer from '../admin/AdminContainer'
import Header, { ExternalLink } from './Header'
import MainLink from './MainLink'

const UserInterface = ({ children }: { children: ReactNode }) => {
    const location = useLocation()

    let lng = useLangSelected()

    const user = useContext(UserContext)

    const isAdmin = location.pathname.includes('/bva-admin') && location.pathname !== `/${lng}/bva-admin/login`

    const isMember = location.pathname.includes('/bva-membre') && location.pathname !== `/${lng}/bva-membre/login`

    const { t } = useTranslation()

    return (
        <div>
            <Header />
            <Main isConnected={isAdmin || isMember}>
                {isAdmin &&
                location.pathname.includes('bva-admin') &&
                user.adminData &&
                !location.pathname.includes('login') ? (
                    <>
                        <Sidebar>
                            <SidebarLink
                                to={`/${lng}/bva-admin`}
                                className={location.pathname === `/${lng}/bva-admin` ? 'active' : ''}
                            >
                                <FontAwesomeIcon icon={faHomeLg} />{' '}
                                {window.innerWidth > +Breakpoint.LAPTOP_XS.split('p')[0] &&
                                    t('sidebar.admin.dashboard')}
                            </SidebarLink>
                            <SidebarLink
                                to={`/${lng}/bva-admin/utilisateurs`}
                                className={location.pathname.includes(`/${lng}/bva-admin/utilisateurs`) ? 'active' : ''}
                            >
                                <FontAwesomeIcon icon={faUser} />{' '}
                                {window.innerWidth > +Breakpoint.LAPTOP_XS.split('p')[0] && t('sidebar.admin.user')}
                            </SidebarLink>
                            <SidebarLink
                                to={`/${lng}/bva-admin/articles`}
                                className={location.pathname.includes(`/${lng}/bva-admin/articles`) ? 'active' : ''}
                            >
                                <FontAwesomeIcon icon={faNewspaper} />{' '}
                                {window.innerWidth > +Breakpoint.LAPTOP_XS.split('p')[0] && t('sidebar.admin.news')}
                            </SidebarLink>
                            <SidebarLink
                                to={`/${lng}/bva-admin/competitions`}
                                className={location.pathname.includes(`/${lng}/bva-admin/competitions`) ? 'active' : ''}
                            >
                                <FontAwesomeIcon icon={faMedal} />{' '}
                                {window.innerWidth > +Breakpoint.LAPTOP_XS.split('p')[0] &&
                                    t('sidebar.admin.competition')}
                            </SidebarLink>
                            <SidebarLink
                                to={`/${lng}/bva-admin/plannings`}
                                className={location.pathname.includes(`/${lng}/bva-admin/plannings`) ? 'active' : ''}
                            >
                                <FontAwesomeIcon icon={faCalendarDays} />{' '}
                                {window.innerWidth > +Breakpoint.LAPTOP_XS.split('p')[0] &&
                                    t('sidebar.admin.plannings')}
                            </SidebarLink>
                            <SidebarLink
                                to={`/${lng}/bva-admin/bva-bot`}
                                className={location.pathname.includes(`/${lng}/bva-admin/bva-bot`) ? 'active' : ''}
                            >
                                <FontAwesomeIcon icon={faRobot} />
                                {window.innerWidth > +Breakpoint.LAPTOP_XS.split('p')[0] && t('sidebar.admin.bva-bot')}
                            </SidebarLink>
                            <SidebarExternalLink
                                href="https://bva-club.notion.site/Ventes-Club-House-5d1250003d794238ad0a9994f03bde66"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <FontAwesomeIcon icon={faCashRegister} />{' '}
                                {window.innerWidth > +Breakpoint.LAPTOP_XS.split('p')[0] && t('sidebar.admin.notion')}{' '}
                                <ExternalIcon icon={faExternalLink} />
                            </SidebarExternalLink>
                        </Sidebar>
                        <AdminContainer>{children}</AdminContainer>
                    </>
                ) : isMember &&
                  location.pathname.includes('bva-membre') &&
                  user.memberData &&
                  !location.pathname.includes('login') ? (
                    <>
                        <Sidebar>
                            <SidebarLink
                                to={`${lng}/bva-membre`}
                                className={location.pathname === `/${lng}/bva-membre` ? 'active' : ''}
                            >
                                <FontAwesomeIcon icon={faHomeLg} />{' '}
                                {window.innerWidth > +Breakpoint.LAPTOP_XS.split('p')[0] &&
                                    t('sidebar.admin.dashboard')}
                            </SidebarLink>
                            <SidebarLink
                                to={`/${lng}/bva-membre/modifier-mon-mot-de-passe`}
                                className={
                                    location.pathname === `/${lng}/bva-membre/modifier-mon-mot-de-passe` ? 'active' : ''
                                }
                            >
                                <FontAwesomeIcon icon={faKey} />{' '}
                                {window.innerWidth > +Breakpoint.LAPTOP_XS.split('p')[0] && t('sidebar.admin.password')}
                            </SidebarLink>
                        </Sidebar>
                        <AdminContainer>{children}</AdminContainer>
                    </>
                ) : (
                    children
                )}
            </Main>
            <Footer>
                <p>
                    {t('footer.copyright')}&nbsp;
                    <a href="https://www.linkedin.com/in/melvin-cado/" target="_blank" rel="noreferrer">
                        <strong>Melvin Cado</strong>
                    </a>{' '}
                    -
                    <Link to={`/${lng}/bva-admin`}>
                        <strong>{t('footer.administration')}</strong>
                    </Link>
                </p>
            </Footer>
        </div>
    )
}

const ExternalIcon = styled(FontAwesomeIcon)`
    margin-left: 6px;
    height: 12px;
    position: relative;
    top: -8px;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        margin: 0;
    }
`

const SidebarLink = styled(MainLink)`
    display: flex;
    align-items: center;

    &.active {
        border: none;
    }

    & * {
        margin-right: 8px;

        @media (max-width: ${Breakpoint.TABLET_L}) {
            justify-content: center;
            margin-right: 0;
        }
    }

    &:active {
        transform: scale(0.95);
    }

    @media (max-width: ${Breakpoint.TABLET_L}) {
        justify-content: center;
    }
`

const SidebarExternalLink = styled(ExternalLink)`
    display: flex;
    align-items: center;

    &.active {
        border: none;
    }

    & * {
        margin-right: 8px;
    }

    @media (max-width: ${Breakpoint.TABLET_L}) {
        justify-content: center;
    }
`

const Sidebar = styled.div`
    display: flex;
    flex-direction: column;
    width: 15%;
    background-color: white;
    min-height: calc(100vh - 166px);
    box-shadow: 0px 0px 4px black;
    box-sizing: border-box;
    padding: 16px;
    padding-left: 3%;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        padding: 24px 0;
    }
`

const Main = styled.main`
    ${({ isConnected }: { isConnected: boolean }) => isConnected && 'display: flex;'}
    min-height: ${({ isConnected }: { isConnected: boolean }) =>
        isConnected ? 'calc(100vh - 166px)' : 'calc(100vh - 58px)'};
    padding: 108px 0 0 0;
    z-index: 1;
`

const Footer = styled.footer`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px;
    background-color: white;
    color: black;
    font-size: 20px;
    min-height: 58px;
    font-family: 'Roboto';
    box-shadow: 0px 0px 4px black;
    position: relative;
    z-index: 2;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 16px;
    }

    & p {
        margin: 0;
    }

    & a {
        color: var(--primary-color);
        text-decoration: none;
    }

    & strong {
        font-family: 'Roboto Semi Bold';
        border-bottom: 1px solid var(--primary-color);
    }
`

export default UserInterface
